<script>
</script>

<svelte:head>
	<title>Security & Compliance - OS Studio</title>
	<meta name="description" content="OS Studio security practices, certifications, and compliance standards including HIPAA, SOC 2, GDPR, and ISO 27001." />
</svelte:head>

<div class="min-h-screen bg-white">
	<!-- Header -->
	<div class="bg-gradient-to-br from-purple-900 to-blue-900 text-white py-16">
		<div class="container mx-auto px-4 max-w-4xl">
			<h1 class="text-5xl font-bold mb-4">Security & Compliance</h1>
			<p class="text-xl text-purple-100">Our commitment to protecting your data and meeting global compliance standards</p>
		</div>
	</div>

	<!-- Content -->
	<div class="container mx-auto px-4 max-w-4xl py-16">
		<div class="prose prose-lg max-w-none">
			<h2>Executive Summary</h2>

			<p>
				OS Studio is built with security and compliance at its core. We implement industry-leading practices, maintain multiple security certifications, and undergo regular audits to ensure your data and infrastructure remain protected.
			</p>

			<h2>1. Security Infrastructure</h2>

			<h3>1.1 Data Encryption</h3>

			<ul>
				<li><strong>In Transit:</strong> All data is encrypted using TLS 1.3 with 256-bit keys during transmission</li>
				<li><strong>At Rest:</strong> Data is encrypted at rest using AES-256 encryption</li>
				<li><strong>Key Management:</strong> Encryption keys are managed through industry-standard key management services (AWS KMS, Google Cloud KMS, Azure Key Vault)</li>
				<li><strong>Database Encryption:</strong> All database fields containing sensitive data are encrypted</li>
			</ul>

			<h3>1.2 Network Security</h3>

			<ul>
				<li>Firewalls and network segmentation</li>
				<li>DDoS protection via Cloudflare Enterprise</li>
				<li>Web Application Firewall (WAF) with custom rules</li>
				<li>Intrusion Detection & Prevention Systems (IDS/IPS)</li>
				<li>VPN and secure tunnels for admin access</li>
				<li>Regular port and vulnerability scans</li>
			</ul>

			<h3>1.3 Authentication & Access Control</h3>

			<ul>
				<li><strong>MFA:</strong> Multi-factor authentication required for all admin and sensitive operations</li>
				<li><strong>SSO:</strong> SAML 2.0 and OIDC support for enterprise customers</li>
				<li><strong>RBAC:</strong> Role-Based Access Control with granular permissions</li>
				<li><strong>Session Management:</strong> Secure session tokens with short expiration times</li>
				<li><strong>Password Policies:</strong> bcrypt hashing with salt; minimum 12-character requirements</li>
				<li><strong>API Keys:</strong> Separate API key management with key rotation</li>
			</ul>

			<h3>1.4 Application Security</h3>

			<ul>
				<li><strong>Code Review:</strong> All code changes undergo security review before deployment</li>
				<li><strong>OWASP Top 10:</strong> Protection against all OWASP Top 10 vulnerabilities</li>
				<li><strong>Input Validation:</strong> Strict input validation and sanitization</li>
				<li><strong>Output Encoding:</strong> Protection against XSS attacks</li>
				<li><strong>SQL Injection:</strong> Parameterized queries and ORM frameworks</li>
				<li><strong>CSRF Protection:</strong> Token-based CSRF protection on all state-changing requests</li>
				<li><strong>Security Headers:</strong> Strict-Transport-Security, CSP, X-Frame-Options, X-Content-Type-Options</li>
			</ul>

			<h3>1.5 Infrastructure Security</h3>

			<ul>
				<li><strong>Server Hardening:</strong> Minimal services running, regular OS patching</li>
				<li><strong>Container Security:</strong> Signed and scanned Docker images, runtime protection</li>
				<li><strong>Vulnerability Scanning:</strong> Automated scanning of dependencies and base images</li>
				<li><strong>Patch Management:</strong> Security patches applied within 48 hours of release</li>
				<li><strong>Privileged Access:</strong> Just-in-time (JIT) admin access, no standing privileges</li>
				<li><strong>Secrets Management:</strong> All secrets stored in secure vaults, never in code</li>
			</ul>

			<h2>2. Compliance Certifications</h2>

			<h3>2.1 SOC 2 Type II</h3>

			<p>
				<strong>Status:</strong> Certified (Annual Audit)
			</p>
			<p>
				OS Studio has successfully completed SOC 2 Type II audits, demonstrating compliance with:
			</p>
			<ul>
				<li>Security (C1-C7)</li>
				<li>Availability (A1-A2)</li>
				<li>Processing Integrity (PI1-PI2)</li>
				<li>Confidentiality (C1-C2)</li>
				<li>Privacy (P1-P8)</li>
			</ul>

			<h3>2.2 GDPR Compliance</h3>

			<p>
				<strong>Status:</strong> Fully Compliant
			</p>
			<p>
				We fully comply with the European Union's General Data Protection Regulation:
			</p>
			<ul>
				<li><strong>Legal Basis:</strong> Contract performance, legitimate interests, and consent</li>
				<li><strong>Data Processing:</strong> Data Processing Agreements available for all customers</li>
				<li><strong>Data Subject Rights:</strong> We support all GDPR rights (access, deletion, portability, objection)</li>
				<li><strong>Data Transfers:</strong> EU-US Data Privacy Framework and Standard Contractual Clauses</li>
				<li><strong>DPO:</strong> Dedicated Data Protection Officer available for inquiries</li>
				<li><strong>Privacy by Design:</strong> Built into all products and processes</li>
			</ul>

			<h3>2.3 HIPAA Compliance</h3>

			<p>
				<strong>Status:</strong> Compliant (Business Associate Agreement Available)
			</p>
			<p>
				For healthcare customers, OS Studio is HIPAA-compliant:
			</p>
			<ul>
				<li>Administrative Safeguards: Policies, access controls, training</li>
				<li>Physical Safeguards: Secure data centers, access logs, facility controls</li>
				<li>Technical Safeguards: Encryption, audit logs, integrity controls</li>
				<li>Breach Notification: Immediate notification protocol</li>
				<li>Business Associate Agreement (BAA): Available for covered entities</li>
			</ul>

			<h3>2.4 CCPA / CPRA Compliance</h3>

			<p>
				<strong>Status:</strong> Fully Compliant
			</p>
			<p>
				We comply with California's Consumer Privacy Act and Privacy Rights Act:
			</p>
			<ul>
				<li>Consumer rights to access, delete, and opt-out</li>
				<li>Transparent privacy policies and practices</li>
				<li>No unauthorized data sales or sharing</li>
				<li>Annual compliance verification</li>
			</ul>

			<h3>2.5 ISO 27001</h3>

			<p>
				<strong>Status:</strong> Certification Pending (In Audit)
			</p>
			<p>
				We are pursuing ISO 27001 certification for information security management:
			</p>
			<ul>
				<li>Information Security Policy</li>
				<li>Risk Assessment & Management</li>
				<li>Access Control</li>
				<li>Cryptography & Encryption</li>
				<li>Physical & Environmental Security</li>
				<li>Incident Management</li>
				<li>Business Continuity</li>
			</ul>

			<h3>2.6 PCI DSS Compliance</h3>

			<p>
				<strong>Status:</strong> Compliant (For Payment Processing)
			</p>
			<p>
				For all payment processing, we maintain PCI DSS Level 1 compliance:
			</p>
			<ul>
				<li>Secure network architecture</li>
				<li>Protection of cardholder data</li>
				<li>Vulnerability management program</li>
				<li>Access control measures</li>
				<li>Regular monitoring and testing</li>
			</ul>

			<h2>3. Security Testing & Audits</h2>

			<h3>3.1 Regular Security Assessments</h3>

			<ul>
				<li><strong>Penetration Testing:</strong> Quarterly third-party pen tests</li>
				<li><strong>Vulnerability Scanning:</strong> Weekly automated scanning</li>
				<li><strong>Code Security Analysis:</strong> SAST/DAST on every deployment</li>
				<li><strong>Dependency Scanning:</strong> Daily checks for vulnerable dependencies</li>
				<li><strong>Infrastructure Audits:</strong> Semi-annual external audits</li>
			</ul>

			<h3>3.2 Bug Bounty Program</h3>

			<p>
				We operate a responsible disclosure program. Security researchers can report vulnerabilities confidentially at <a href="mailto:security@osstudio.com">security@osstudio.com</a>.
			</p>

			<h2>4. Data Center & Infrastructure</h2>

			<h3>4.1 Hosting</h3>

			<p>
				OS Studio runs on trusted cloud infrastructure:
			</p>
			<ul>
				<li><strong>Primary:</strong> AWS Regions (us-east-1, us-west-2, eu-west-1, ap-southeast-1)</li>
				<li><strong>Backup:</strong> Google Cloud Platform for disaster recovery</li>
				<li>ISO 27001 certified data centers</li>
				<li>Redundant power, cooling, and network connectivity</li>
			</ul>

			<h3>4.2 Disaster Recovery & Business Continuity</h3>

			<ul>
				<li><strong>RTO:</strong> Recovery Time Objective: 4 hours</li>
				<li><strong>RPO:</strong> Recovery Point Objective: 1 hour</li>
				<li>Automated backups every hour</li>
				<li>Cross-region replication</li>
				<li>Annual disaster recovery testing</li>
				<li>Incident response playbooks and procedures</li>
			</ul>

			<h2>5. Monitoring & Logging</h2>

			<h3>5.1 Continuous Monitoring</h3>

			<ul>
				<li>Real-time monitoring of all systems and applications</li>
				<li>Automated alerting for security events</li>
				<li>24/7 security operations center (SOC) monitoring</li>
				<li>Network traffic analysis and anomaly detection</li>
				<li>Database activity monitoring</li>
			</ul>

			<h3>5.2 Audit Logging</h3>

			<ul>
				<li>Comprehensive audit logs of all access and changes</li>
				<li>Immutable logging to prevent tampering</li>
				<li>Logs retained for minimum 2 years</li>
				<li>Regularly reviewed for security incidents</li>
				<li>Available for your own audit and compliance review</li>
			</ul>

			<h2>6. Employee & Vendor Security</h2>

			<h3>6.1 Employee Security</h3>

			<ul>
				<li>Background checks for all employees</li>
				<li>Mandatory security training and awareness</li>
				<li>Access control based on least privilege principle</li>
				<li>Device encryption and endpoint protection</li>
				<li>Confidentiality agreements and NDAs</li>
			</ul>

			<h3>6.2 Vendor Management</h3>

			<ul>
				<li>Vetted third-party service providers</li>
				<li>Data Processing Agreements with all vendors</li>
				<li>Regular vendor security assessments</li>
				<li>Restrictions on subcontractor use</li>
			</ul>

			<h2>7. Incident Response</h2>

			<h3>7.1 Incident Response Plan</h3>

			<p>
				We maintain a comprehensive incident response plan:
			</p>
			<ul>
				<li>24/7 incident response team</li>
				<li>Immediate investigation and containment</li>
				<li>Customer notification within 72 hours of confirmed breach</li>
				<li>Post-incident reviews and improvements</li>
				<li>Transparent communication throughout the process</li>
			</ul>

			<h3>7.2 Reporting</h3>

			<p>
				In the event of a security incident affecting your data, we will:
			</p>
			<ul>
				<li>Notify you immediately (within 24 hours if confirmed)</li>
				<li>Provide details about the incident and scope</li>
				<li>Offer assistance and resources</li>
				<li>Provide regular updates on investigation and remediation</li>
			</ul>

			<h2>8. Security Policies & Procedures</h2>

			<p>
				Our security program includes:
			</p>
			<ul>
				<li>Information Security Policy</li>
				<li>Access Control Policy</li>
				<li>Encryption Standards</li>
				<li>Change Management Procedures</li>
				<li>Incident Response Plan</li>
				<li>Business Continuity & Disaster Recovery Plan</li>
				<li>Vendor Management Policy</li>
				<li>Employee Security Training Program</li>
			</ul>

			<p>
				Policies are reviewed and updated annually or when significant changes occur.
			</p>

			<h2>9. Vulnerability Disclosure</h2>

			<p>
				We take security vulnerabilities seriously. If you discover a vulnerability, please report it to:
			</p>
			<ul>
				<li><strong>Email:</strong> <a href="mailto:security@osstudio.com">security@osstudio.com</a></li>
				<li><strong>PGP Key:</strong> Available upon request</li>
				<li><strong>Response Time:</strong> Initial response within 24 hours</li>
				<li><strong>Confidentiality:</strong> We maintain strict confidentiality during investigation</li>
			</ul>

			<p>
				Do NOT disclose vulnerabilities publicly. We are committed to working with security researchers to resolve issues responsibly.
			</p>

			<h2>10. Compliance & Audit Cooperation</h2>

			<p>
				We support your compliance requirements:
			</p>
			<ul>
				<li>Provide audit logs and security documentation</li>
				<li>Execute Data Processing Agreements (DPA) for GDPR</li>
				<li>Execute Business Associate Agreements (BAA) for HIPAA</li>
				<li>Participate in customer security audits (within reasonable scope)</li>
				<li>Provide SOC 2 reports and certifications</li>
				<li>Support compliance questionnaires (CAIQ, NIST)</li>
			</ul>

			<h2>11. Contact Security Team</h2>

			<p>
				For security questions, concerns, or vulnerability reports:
			</p>
			<ul>
				<li><strong>Security Issues:</strong> <a href="mailto:security@osstudio.com">security@osstudio.com</a></li>
				<li><strong>Compliance Questions:</strong> <a href="mailto:compliance@osstudio.com">compliance@osstudio.com</a></li>
				<li><strong>Legal Agreements:</strong> <a href="mailto:legal@osstudio.com">legal@osstudio.com</a></li>
				<li><strong>Response Time:</strong> Within 24 hours</li>
			</ul>

		</div>
	</div>
</div>

<style lang="postcss">
	:global(body) {
		margin: 0;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
		background: white;
		color: #1a1a1a;
	}

	:global(html, body) {
		width: 100%;
	}

	:global(.prose) {
		--prose-body: #374151;
		--prose-headings: #111827;
		--prose-links: #9333ea;
	}

	:global(.prose h2) {
		@apply text-3xl font-bold text-gray-900 mt-12 mb-4;
	}

	:global(.prose h3) {
		@apply text-2xl font-bold text-gray-900 mt-8 mb-3;
	}

	:global(.prose p) {
		@apply text-gray-700 mb-4 leading-relaxed;
	}

	:global(.prose ul) {
		@apply list-disc list-inside mb-4 space-y-2;
	}

	:global(.prose ul li) {
		@apply text-gray-700;
	}

	:global(.prose a) {
		@apply text-purple-600 font-semibold;
	}

	:global(.prose a:hover) {
		@apply text-purple-700;
	}
</style>
